import {
  clearWinChronicle,
  getRecentWinRecords,
  type CompletedRunRecord,
} from "@dawn/adapters";
import { getSiteStrings } from "@dawn/i18n";
import { formatChronicleEntry } from "@site/config/careerCopy";
import {
  useEscapeToDismiss,
  useLandingModalFocusTrap,
  useLandingModalState,
} from "@site/modals/landingModalHelpers";
import { useLocale } from "@site/LocaleContext";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import styles from "@site/modals/LandingChronicleModal.module.css";

interface LandingChronicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLElement | null>;
  restoreFocus?: boolean;
  onCleared?: () => void;
}

export function LandingChronicleModal({
  isOpen,
  onClose,
  returnFocusRef,
  restoreFocus = true,
  onCleared,
}: LandingChronicleModalProps) {
  const { locale } = useLocale();
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [records, setRecords] = useState<CompletedRunRecord[]>(() => getRecentWinRecords());
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const clearConfirmButtonRef = useRef<HTMLButtonElement>(null);
  const clearCancelButtonRef = useRef<HTMLButtonElement>(null);

  const hideClearConfirm = useCallback((): void => {
    setIsClearConfirmOpen(false);
  }, []);

  const { panelRef } = useLandingModalState(isOpen, returnFocusRef, restoreFocus);

  const site = getSiteStrings(locale);

  const dismiss = useCallback((): void => {
    if (isClearConfirmOpen) {
      hideClearConfirm();
      closeButtonRef.current?.focus();
      return;
    }

    onClose();
  }, [hideClearConfirm, isClearConfirmOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setRecords(getRecentWinRecords());
      hideClearConfirm();
      return;
    }

    hideClearConfirm();
  }, [hideClearConfirm, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isClearConfirmOpen) {
      clearConfirmButtonRef.current?.focus();
      return;
    }

    closeButtonRef.current?.focus();
  }, [isClearConfirmOpen, isOpen]);

  useLandingModalFocusTrap(panelRef, isOpen);
  useEscapeToDismiss(isOpen, dismiss);

  const handleClearClick = (): void => {
    setIsClearConfirmOpen(true);
  };

  const handleClearConfirm = (): void => {
    clearWinChronicle();
    onCleared?.();
    onClose();
  };

  const handleClearCancel = (): void => {
    hideClearConfirm();
    closeButtonRef.current?.focus();
  };

  return (
    <div
      id="chronicle-modal"
      className={styles.modal}
      data-testid="chronicle-modal"
      hidden={!isOpen}
    >
      <div
        className={styles.backdrop}
        data-testid="chronicle-modal-backdrop"
        onClick={dismiss}
      />
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chronicle-modal-title"
      >
        <div
          className={isClearConfirmOpen ? styles.viewHidden : styles.view}
          data-testid="chronicle-modal-view"
        >
          <h2
            id="chronicle-modal-title"
            className={styles.title}
            data-testid="chronicle-modal-title"
          >
            {site.landing.chronicleModalTitle}
          </h2>
          <ul className={styles.list} data-testid="chronicle-modal-list">
            {records.map((record) => (
              <li
                key={`${record.completedAt}-${record.clanName}-${record.turns}`}
                className={styles.entry}
              >
                {formatChronicleEntry(
                  site.landing.chronicleEntryFormat,
                  record,
                  locale,
                )}
              </li>
            ))}
          </ul>
          <button
            ref={clearButtonRef}
            className={`site-link ${styles.clearButton}`}
            type="button"
            data-testid="chronicle-modal-clear"
            onClick={handleClearClick}
          >
            {site.landing.chronicleClearWinHistoryLabel}
          </button>
          <button
            ref={closeButtonRef}
            className={styles.closeButton}
            type="button"
            data-testid="chronicle-modal-close"
            onClick={onClose}
          >
            {site.landing.chronicleModalCloseLabel}
          </button>
        </div>
        <div
          className={isClearConfirmOpen ? styles.confirmView : styles.confirmViewHidden}
          data-testid="chronicle-modal-clear-confirm-view"
        >
          <p className={styles.confirmBody} data-testid="chronicle-modal-clear-body">
            {site.landing.chronicleClearWinHistoryBody}
          </p>
          <div className={styles.confirmActions}>
            <button
              ref={clearConfirmButtonRef}
              className={styles.confirmButton}
              type="button"
              data-testid="chronicle-modal-clear-yes"
              onClick={handleClearConfirm}
            >
              {site.landing.chronicleClearWinHistoryConfirm}
            </button>
            <button
              ref={clearCancelButtonRef}
              className={styles.cancelButton}
              type="button"
              data-testid="chronicle-modal-clear-cancel"
              onClick={handleClearCancel}
            >
              {site.landing.chronicleClearWinHistoryCancel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
